import { useContext, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Types } from '@cornerstonejs/core';
import type { Types as CornerstoneTypes } from '@cornerstonejs/core';
import { utilities as toolsUtilities, annotation } from '@cornerstonejs/tools';
import {
  mapAnnotationToMeasurement,
  selectAnnotation,
  selectSelectedAnnotationUID,
} from '@/state/slices/measurementSession';
import type { AppDispatch } from '@/state/store';
import {
  useGetStudyReportsQuery,
  useGetWorksheetQuery,
} from '@/state/slices/reports';
import { useGetWorksheetCanonicalMeasurementsQuery } from '@/state/slices/options';
import { SettingsContext, UserContext } from '@/state/contexts';
import { usePermissions } from '@/hooks/usePermissions';
import { selectAIVariants } from '@/features/report';
import type { MeasurementGroup } from '@/models/template';
import {
  aiHydratedAnnotationUid,
  hydratedAnnotationUid,
} from '../../utils/annotationUid';
import { hydrateProbeAnnotation } from './hydrateProbeAnnotation';
import { hydrateVolumeAnnotation } from './useAnnotationHydration.aiVolume';
import {
  hydrateAiDopplerPoint,
  hydrateAiDopplerVti,
  isDopplerVtiMeasurement,
} from './useAnnotationHydration.aiDoppler';
import { getBaseAnnotationStyle } from '../../utils/annotationStyle';
import { perpendicularLabelPosition } from '../../tools/labelPlacement';
import { triggerAllViewports } from '../../utils/triggerAllViewports';
import { loadShown } from '../MeasurementList/utils/listStorage';

const HOVER_LABEL_OFFSET_WORLD = 30;

/** canonicalMeasurement uuids the worksheet defines a row for. */
export function worksheetCanonicals(
  worksheet: { measurementGroups?: MeasurementGroup[] } | undefined
): Set<string> {
  const keys = new Set<string>();
  worksheet?.measurementGroups?.forEach((group) =>
    group.measurementLabels.forEach((ml) => {
      if (ml.canonicalMeasurement) keys.add(ml.canonicalMeasurement);
    })
  );
  return keys;
}

/** AI lines on A4C are keypoint measurements; they start hidden until the
 *  reader shows them in the measurement list (EMv2 channel). */
export function aiLineStartsHidden(
  view: string | undefined,
  uid: string,
  shown: Set<string>
): boolean {
  return view === 'A4C' && !shown.has(uid);
}

interface UseAnnotationHydrationProps {
  viewport: CornerstoneTypes.IStackViewport | Types.IVideoViewport | null;
  viewportId: string;
  studyUuid: string;
  dicomUuid: string | undefined;
  /** The video URL for this DICOM — fallback FrameOfReferenceUID only. */
  videoUrl: string | null;
  /** Stack imageId (`web:...`) when this DICOM is a single-frame still; null for videos. */
  stackImageId: string | null;
  /** Viewport loaded (setVideo done) so the real FoR — videoElement.src — is set. */
  isViewportReady: boolean;
  /**
   * useViewportSetup's "image currently loaded on the viewport belongs to this
   * DICOM" marker. On same-type navigation the viewport is REUSED — the old
   * image stays mounted while the new one loads async — so this effect can fire
   * with the new `dicomUuid` but the old image still on the viewport. Hydrating
   * then anchors overlays to the wrong `referencedImageId` and latches. Gate on
   * this ref so hydration waits for the real image.
   */
  loadedDicomUuidRef: React.MutableRefObject<string | undefined>;
  /** Re-runs hydration when the viewport swap settles (ref updates alongside). */
  isLoadingViewport: boolean;
  /**
   * mm/pixel from DICOM metadata. Needed so AI volume hydration can seed
   * area (cm²) and long-axis length (mm) into the textbox before the user
   * edits the contour — without it the Simpson computation collapses to
   * pixel-space values.
   */
  pixelSpacing?: number;
  /** The DICOM's AI view label (e.g. "A4C"), when classified. */
  view?: string;
}

/**
 * Hydrates annotations for this viewport's DICOM from the report data.
 * Each ViewerPane calls this independently — no shared state, no queues.
 * Reads report + worksheet from RTK Query cache (no extra fetch).
 */
export function useAnnotationHydration({
  viewport,
  viewportId,
  studyUuid,
  dicomUuid,
  videoUrl,
  loadedDicomUuidRef,
  isLoadingViewport,
  stackImageId,
  isViewportReady,
  pixelSpacing,
  view,
}: UseAnnotationHydrationProps): void {
  const dispatch = useDispatch<AppDispatch>();
  // Mirror the selected annotation UID in a ref so hydration can honor a
  // selection made *before* the annotation exists — e.g. clicking a worksheet
  // row for a measurement on another dicom dispatches selectAnnotation, then
  // navigation re-hydrates that annotation here. Without this, hydration's base
  // style would clobber the selection and the line would render un-selected.
  // A ref (not a dep) keeps hydration from re-running on every selection change.
  const selectedAnnotationUID = useSelector(selectSelectedAnnotationUID);
  const selectedAnnotationUIDRef = useRef<string | null>(selectedAnnotationUID);
  selectedAnnotationUIDRef.current = selectedAnnotationUID;
  const { data: reportsPagination } = useGetStudyReportsQuery(studyUuid, {
    skip: !studyUuid,
  });
  const report = reportsPagination?.results?.[0];
  const { canEdit } = usePermissions({ isFinalized: !!report?.isFinalized });

  const { settings } = useContext(SettingsContext);
  const { user } = useContext(UserContext);
  const userId = user?.id ?? 'anonymous';
  const { data: worksheet } = useGetWorksheetQuery(
    settings.defaultWorksheet || '',
    { skip: !settings.defaultWorksheet }
  );
  const { data: canonicalMeasurements } =
    useGetWorksheetCanonicalMeasurementsQuery();

  // Track what we've already hydrated: "reportUuid:dicomUuid:worksheetUuid"
  const hydratedKeyRef = useRef<string | null>(null);

  // Re-sync lock state for already-hydrated annotations when canEdit flips
  // (sign / open-addendum mid-session). The main effect below early-returns
  // on existing UIDs so its per-annotation locks only fire on first hydration.
  useEffect(() => {
    if (!report?.uuid) return;
    const all = annotation.state.getAllAnnotations();
    all.forEach((ann) => {
      const uid = ann.annotationUID;
      if (!uid) return;
      if (
        uid.startsWith(`hydrated-${studyUuid}-`) ||
        uid.startsWith(`ai-hydrated-${studyUuid}-`)
      ) {
        annotation.locking.setAnnotationLocked(uid, !canEdit);
      }
    });
  }, [canEdit, studyUuid, report?.uuid]);

  // When this pane leaves its dicom, the dicom's annotations STAY in
  // Cornerstone state (AP-773). Cornerstone keys them by image, so an
  // off-screen clip keeps its drawings for free, and every unsaved reshape or
  // adoption is still there when the clip comes back — for the reader and for
  // an undo that navigates back to it. The hydration effect below skips UIDs
  // that already exist, so a post-save refetch never overwrites local edits.
  // (Before AP-773 the annotations were removed here and re-created from the
  // server payload on return, which silently reverted unsaved edits.)
  //
  // What still happens on the way out: selection means "a visible trace" (I7
  // turn-off rules), so when the selected annotation's dicom leaves the pane,
  // the worksheet's bold row must not outlive it.
  useEffect(() => {
    if (!studyUuid || !dicomUuid) return undefined;
    return () => {
      const selected = selectedAnnotationUIDRef.current;
      if (!selected) return;
      const a = annotation.state.getAnnotation(selected) as
        | { metadata?: { referencedImageId?: string } }
        | undefined;
      if (a?.metadata?.referencedImageId?.includes(dicomUuid)) {
        dispatch(selectAnnotation(null));
      }
    };
  }, [studyUuid, dicomUuid, dispatch]);

  // Switching the worksheet in settings re-evaluates what may be drawn: drop AI
  // annotations the new one has no row for. The main effect re-adds any it does.
  useEffect(() => {
    if (!studyUuid || !worksheet?.measurementGroups) return;
    const allowed = worksheetCanonicals(worksheet);
    const aiPrefix = `ai-hydrated-${studyUuid}-`;
    let removed = false;
    annotation.state.getAllAnnotations().forEach((a) => {
      const uid = a.annotationUID;
      if (!uid?.startsWith(aiPrefix)) return;
      if (allowed.has(uid.slice(aiPrefix.length))) return;
      annotation.state.removeAnnotation(uid);
      removed = true;
    });
    if (removed) triggerAllViewports();
  }, [worksheet, studyUuid]);

  // If the canonical-measurements query resolves after hydration ran, patch
  // any hydrated annotations that are missing `data.abbreviation`. The main
  // effect's `hydratedKeyRef` guard blocks re-hydration for the same report,
  // so without this the hover chip would stay empty until the next study.
  useEffect(() => {
    if (!canonicalMeasurements?.length || !studyUuid) return;
    const abbrByUuid = new Map<string, string>();
    canonicalMeasurements.forEach((c) =>
      abbrByUuid.set(c.uuid, c.abbreviation)
    );
    const humanPrefix = `hydrated-${studyUuid}-`;
    const aiPrefix = `ai-hydrated-${studyUuid}-`;
    const all = annotation.state.getAllAnnotations();
    all.forEach((ann) => {
      const uid = ann.annotationUID;
      if (!uid) return;
      let canonicalUuid: string | null = null;
      if (uid.startsWith(aiPrefix)) canonicalUuid = uid.slice(aiPrefix.length);
      else if (uid.startsWith(humanPrefix))
        canonicalUuid = uid.slice(humanPrefix.length);
      if (!canonicalUuid) return;
      const data = ann.data as Record<string, unknown> | undefined;
      if (!data || data.abbreviation) return;
      const abbr = abbrByUuid.get(canonicalUuid);
      if (abbr) data.abbreviation = abbr;
    });
  }, [canonicalMeasurements, studyUuid]);

  useEffect(() => {
    if (
      !viewport ||
      !isViewportReady ||
      !dicomUuid ||
      !report?.uuid ||
      !report.measurements
    )
      return;

    const key = `${report.uuid}:${dicomUuid}:${worksheet?.uuid ?? ''}`;
    if (hydratedKeyRef.current === key) return;

    // The viewport can lag a DICOM switch: on the same-type reuse path the
    // previous viewport stays mounted while its image is swapped async, so this
    // effect fires with the new dicomUuid but the OLD image still loaded.
    // Hydrating then anchors overlays to the wrong referencedImageId and
    // latches — they never appear until a hard refresh. Wait until the setup
    // hook confirms THIS DICOM's image is the one on the viewport.
    if (loadedDicomUuidRef.current !== dicomUuid) return;

    // Use the viewport's real FoR so rehydrated annotations match the render
    // group; videoUrl/stackImageId are fallbacks (stack FoR is undefined for stills).
    const frameOfReferenceUID =
      viewport.getFrameOfReferenceUID() || videoUrl || stackImageId;
    if (!frameOfReferenceUID) return;

    // Build canonicalMeasurement → label lookup from worksheet
    const classToLabel = new Map<string, string>();
    if (worksheet?.measurementGroups) {
      worksheet.measurementGroups.forEach((group) => {
        group.measurementLabels.forEach((ml) => {
          if (ml.canonicalMeasurement) {
            classToLabel.set(ml.canonicalMeasurement, ml.label);
          }
        });
      });
    }

    // A scan reports every measurement the model produces, not just this
    // worksheet's (RA volume, AOVAD, …); those have no row, so drawing them is
    // clutter. Human annotations stay — a reader never loses their own work.
    const allowedCanonicals = worksheetCanonicals(worksheet);

    // canonicalMeasurement uuid → abbreviation (e.g. "RVEDD"). Consumed by
    // the textBox callback in useSharedToolState for the hover chip.
    const classToAbbreviation = new Map<string, string>();
    (canonicalMeasurements ?? []).forEach((c) => {
      classToAbbreviation.set(c.uuid, c.abbreviation);
    });

    const camera = viewport.getCamera();
    // Doppler stills are stack viewports (imageId `web:…`), not `video:…`.
    // Cornerstone renders an annotation only on the image whose id matches
    // referencedImageId, so doppler overlays must use the actual current id.
    const aiStackImageId =
      (
        viewport as unknown as {
          getCurrentImageId?: () => string | undefined;
        }
      ).getCurrentImageId?.() ?? '';

    // Doppler stills need a real stack imageId for `referencedImageId` and the
    // viewport's imageData for the native-pixel→world transform. If either is
    // missing the overlay registers against a bogus id / lands at (0,0) and
    // renders nothing — yet hydratedKeyRef still latches below, permanently
    // blocking re-hydration. Defer until the viewport is ready.
    //
    // Covers AI doppler (POINT peak-velocity + FREEHAND VTI envelopes) and human
    // POINT probes — both anchor to the stack imageId. Multi-frame sweeps are
    // VIDEO viewports (videoUrl set): they anchor to `video:<dicom>/frames/<N>`
    // (see resolveDopplerImageId) so they are exempt from the imageId gate, but
    // AI coords still need imageData for pixelToWorld.
    const hasAiDoppler = (report.aiMeasurements ?? []).some(
      (ai) =>
        !ai.isDeleted &&
        ai.annotation &&
        ai.annotation.dicom === dicomUuid &&
        ((ai.annotation.type === 'FREEHAND' && isDopplerVtiMeasurement(ai)) ||
          ai.annotation.type === 'POINT')
    );
    const hasHumanProbe = report.measurements.some(
      (m) =>
        m.annotation?.type === 'POINT' &&
        !!m.canonicalMeasurement &&
        m.annotation.dicomUuid === dicomUuid &&
        m.annotation.points.length > 0
    );
    if ((hasAiDoppler || hasHumanProbe) && !aiStackImageId && !videoUrl) return;

    // AI doppler coords are native pixels converted via pixelToWorld, which
    // reads viewport.getImageData().imageData. Until that's loaded the
    // conversion returns null and hydration adds nothing — defer so the latch
    // at the end of the effect doesn't fire on an empty pass.
    const imageDataReady = !!(
      viewport as unknown as {
        getImageData?: () => { imageData?: unknown } | undefined;
      }
    ).getImageData?.()?.imageData;
    if (hasAiDoppler && !imageDataReady) return;

    let count = 0;

    // Canonical keys of human-annotated measurements — AI hydration skips
    // these so the adopted human line wins.
    const humanAnnotatedKeys = new Set(
      report.measurements
        .filter((m) => !!m.annotation && !!m.canonicalMeasurement)
        .map((m) => m.canonicalMeasurement)
    );

    // Ranked AI variants — ONE annotated variant per canonical (lowest
    // priority wins; see selectAIVariants). The row model, click navigation
    // and hydration must all resolve to the SAME variant: hydrating by payload
    // order instead drew one variant while a label click navigated to
    // another's frame (AP-765 — RA major systolic frame 4 vs diastolic 29).
    const aiVariantRoles = selectAIVariants(report.aiMeasurements ?? []);
    const aiAnnotatedVariants = [...aiVariantRoles.values()]
      .map((roles) => roles.annotated)
      .filter((ai): ai is NonNullable<typeof ai> => !!ai);

    // Filter for this DICOM only. POINT annotations (single-click probes) are
    // handled in the separate branch below using VelocityProbeTool; this path
    // only rebuilds Length/Angle/Freehand tools which all need ≥2 handle points.
    report.measurements
      .filter(
        (m) =>
          m.annotation &&
          m.annotation.type !== 'POINT' &&
          m.canonicalMeasurement &&
          m.annotation.dicomUuid === dicomUuid
      )
      .forEach((m) => {
        try {
          // UIDs are study-scoped so annotations from a prior study can't
          // collide with (or block) the current study's hydration.
          const uid = hydratedAnnotationUid(studyUuid, m.canonicalMeasurement);

          // Skip if already exists in Cornerstone (by hydrate UID)
          if (annotation.state.getAnnotation(uid)) return;

          const label =
            classToLabel.get(m.canonicalMeasurement) ?? m.canonicalMeasurement;

          // Skip if an annotation with the same label already exists on the
          // same FrameOfReferenceUID (i.e. same video). Scoping by FoR lets
          // labels repeat across studies without blocking each other.
          const allAnnotations = annotation.state.getAllAnnotations();
          const alreadyDrawn = allAnnotations.some(
            (a) =>
              (a.data as Record<string, unknown>)?.measurementLabel === label &&
              (a.metadata as unknown as Record<string, unknown>)
                ?.FrameOfReferenceUID === frameOfReferenceUID
          );
          if (alreadyDrawn) return;

          // Stills render on a STACK viewport — its display filter matches the
          // stack imageId and its stats key uses the imageId: prefix.
          const referencedImageId =
            stackImageId ??
            `video:${dicomUuid}/frames/${m.annotation!.frameNumber}`;
          const statsKey = stackImageId
            ? `imageId:${referencedImageId}`
            : `videoId:${referencedImageId}`;

          // FREEHAND traces (Simpson volumes / areas) hydrate through the
          // shared volume hydrator so the disks overlay and Vol/L textbox
          // come back after refresh, same as AI volumes.
          if (m.annotation!.type === 'FREEHAND') {
            const added = hydrateVolumeAnnotation({
              uid,
              label,
              isAI: false,
              canonicalMeasurement: m.canonicalMeasurement,
              value: m.value ?? null,
              unit: m.unit ?? '',
              points: m.annotation!.points,
              dicomUuid,
              frameOfReferenceUID,
              refImageId: referencedImageId,
              statsKey,
              reorderOutline: false,
              viewport,
              camera,
              canEdit,
              pixelSpacing,
              dispatch,
              frameNumber: m.annotation!.frameNumber,
              abbreviation: classToAbbreviation.get(m.canonicalMeasurement),
              isSelected: selectedAnnotationUIDRef.current === uid,
            });
            if (added) count += 1;
            return;
          }

          const toolName = m.annotation!.type === 'ANGLE' ? 'Angle' : 'Length';

          const lengthMm =
            m.unit === 'cm' ? (m.value ?? 0) * 10 : (m.value ?? 0);

          const points = m.annotation!.points.map(
            (p) => [...p] as Types.Point3
          );

          const mid: Types.Point3 = [
            ((points[0]?.[0] ?? 0) + (points[1]?.[0] ?? 0)) / 2,
            ((points[0]?.[1] ?? 0) + (points[1]?.[1] ?? 0)) / 2,
            0,
          ];
          // Fix the hover chip on a normal line from the midpoint so it
          // doesn't overlap the annotation itself. hasMoved=true stops
          // Cornerstone's auto-placer.
          const labelPos = perpendicularLabelPosition(
            points[0],
            points[1],
            HOVER_LABEL_OFFSET_WORLD
          );

          annotation.state.addAnnotation(
            {
              annotationUID: uid,
              data: {
                handles: {
                  points,
                  activeHandleIndex: null,
                  textBox: {
                    hasMoved: true,
                    worldPosition: labelPos,
                    worldBoundingBox: {
                      topLeft: labelPos,
                      topRight: labelPos,
                      bottomLeft: labelPos,
                      bottomRight: labelPos,
                    },
                  },
                },
                label: '',
                measurementLabel: label,
                abbreviation: classToAbbreviation.get(m.canonicalMeasurement),
                cachedStats: {
                  [statsKey]: {
                    length: lengthMm,
                    unit: 'mm',
                    statsArray: [
                      {
                        name: 'length',
                        value: lengthMm,
                        unit: 'mm',
                        type: 'Linear',
                      },
                    ],
                  },
                },
              },
              highlighted: false,
              autoGenerated: false,
              invalidated: false,
              isLocked: !canEdit,
              isVisible: true,
              metadata: {
                toolName,
                FrameOfReferenceUID: frameOfReferenceUID,
                referencedImageId,
                viewPlaneNormal: [0, 0, 1] as Types.Point3,
                viewUp: camera.viewUp ?? ([0, -1, 0] as Types.Point3),
                cameraFocalPoint: camera.focalPoint ?? mid,
                cameraPosition: camera.position ?? ([0, 0, 0] as Types.Point3),
              },
            },
            viewport.element
          );

          // Cornerstone gates endpoint drag via `annotation.locking`, not the
          // `isLocked` field on the annotation object — this call is the
          // load-bearing one.
          annotation.locking.setAnnotationLocked(uid, !canEdit);

          annotation.config.style.setAnnotationStyles(
            uid,
            getBaseAnnotationStyle(
              { data: { isAI: false } },
              { isSelected: selectedAnnotationUIDRef.current === uid }
            )
          );

          dispatch(
            mapAnnotationToMeasurement({
              annotationUID: uid,
              canonicalMeasurement: m.canonicalMeasurement,
            })
          );

          count += 1;
        } catch {
          // Viewport not fully ready — will retry on next render
        }
      });

    // ── POINT annotations (spectral-Doppler velocity probes) ─────────
    // Each POINT is re-painted as a native VelocityProbeTool annotation so the
    // crosshair + velocity text reappear after a hard refresh. Calibration is
    // rebuilt from viewport metadata — no extra backend call needed.
    report.measurements
      .filter(
        (m) =>
          m.annotation &&
          m.annotation.type === 'POINT' &&
          m.canonicalMeasurement &&
          m.annotation.dicomUuid === dicomUuid &&
          m.annotation.points.length > 0
      )
      .forEach((m) => {
        try {
          const label =
            classToLabel.get(m.canonicalMeasurement) ?? m.canonicalMeasurement;
          const uid = hydratedAnnotationUid(studyUuid, m.canonicalMeasurement);
          const added = hydrateProbeAnnotation({
            measurement: m,
            studyUuid,
            dicomUuid,
            frameOfReferenceUID,
            viewport,
            label,
            abbreviation: classToAbbreviation.get(m.canonicalMeasurement),
            isSelected: selectedAnnotationUIDRef.current === uid,
            canEdit,
            dispatch,
          });
          if (added) count += 1;
        } catch {
          // Viewport not fully ready — will retry on next render
        }
      });

    // ── AI annotations (LINE only, same effect as human) ─────────────
    aiAnnotatedVariants
      .filter(
        (ai) =>
          ai.annotation &&
          ai.annotation.type === 'LINE' &&
          ai.annotation.points.length > 0 &&
          ai.annotation.dicom === dicomUuid &&
          // Skip AI when a human annotation already exists for this key —
          // the adopted human line supersedes the AI one.
          !humanAnnotatedKeys.has(ai.canonicalMeasurement) &&
          allowedCanonicals.has(ai.canonicalMeasurement)
      )
      .forEach((ai) => {
        try {
          const uid = aiHydratedAnnotationUid(
            studyUuid,
            ai.canonicalMeasurement
          );
          if (annotation.state.getAnnotation(uid)) {
            return;
          }
          // Suppress AI re-spawn when the adopted human variant already
          // survives in Cornerstone state. Nav-back can otherwise race the
          // report refetch: hydration runs first with stale measurements,
          // humanAnnotatedKeys is empty, the AI filter passes, and we end
          // up with the original AI + the adopted side by side.
          if (
            annotation.state.getAnnotation(
              hydratedAnnotationUid(studyUuid, ai.canonicalMeasurement)
            )
          )
            return;

          const aiLabel =
            classToLabel.get(ai.canonicalMeasurement) ??
            (ai.name || ai.canonicalMeasurement);

          const ann = ai.annotation!;
          const aiRefImageId =
            stackImageId ?? `video:${dicomUuid}/frames/${ann.frameNumber}`;
          const aiStatsKey = stackImageId
            ? `imageId:${aiRefImageId}`
            : `videoId:${aiRefImageId}`;
          const aiPoints = ann.points.map((p) => [...p] as Types.Point3);

          const aiMid: Types.Point3 = [
            ((aiPoints[0]?.[0] ?? 0) + (aiPoints[1]?.[0] ?? 0)) / 2,
            ((aiPoints[0]?.[1] ?? 0) + (aiPoints[1]?.[1] ?? 0)) / 2,
            0,
          ];
          const aiLabelPos = perpendicularLabelPosition(
            aiPoints[0],
            aiPoints[1],
            HOVER_LABEL_OFFSET_WORLD
          );

          const aiLengthMm =
            ai.unit === 'cm' ? (ai.value ?? 0) * 10 : (ai.value ?? 0);

          annotation.state.addAnnotation(
            {
              annotationUID: uid,
              data: {
                handles: {
                  points: aiPoints,
                  activeHandleIndex: null,
                  textBox: {
                    hasMoved: true,
                    worldPosition: aiLabelPos,
                    worldBoundingBox: {
                      topLeft: aiLabelPos,
                      topRight: aiLabelPos,
                      bottomLeft: aiLabelPos,
                      bottomRight: aiLabelPos,
                    },
                  },
                },
                label: '',
                measurementLabel: `AI: ${aiLabel}`,
                abbreviation: classToAbbreviation.get(ai.canonicalMeasurement),
                isAI: true,
                cachedStats: {
                  [aiStatsKey]: {
                    length: aiLengthMm,
                    unit: 'mm',
                    statsArray: [
                      {
                        name: 'length',
                        value: aiLengthMm,
                        unit: 'mm',
                        type: 'Linear',
                      },
                    ],
                  },
                },
              },
              highlighted: false,
              autoGenerated: false,
              invalidated: false,
              isLocked: !canEdit,
              isVisible: true,
              metadata: {
                toolName: 'Length',
                FrameOfReferenceUID: frameOfReferenceUID,
                referencedImageId: aiRefImageId,
                viewPlaneNormal: [0, 0, 1] as Types.Point3,
                viewUp: camera.viewUp ?? ([0, -1, 0] as Types.Point3),
                cameraFocalPoint: camera.focalPoint ?? aiMid,
                cameraPosition: camera.position ?? ([0, 0, 0] as Types.Point3),
              },
            },
            viewport.element
          );

          annotation.locking.setAnnotationLocked(uid, !canEdit);

          annotation.config.style.setAnnotationStyles(
            uid,
            getBaseAnnotationStyle(
              { data: { isAI: true } },
              { isSelected: selectedAnnotationUIDRef.current === uid }
            )
          );

          if (aiLineStartsHidden(view, uid, loadShown(userId))) {
            annotation.visibility.setAnnotationVisibility(uid, false);
          }

          dispatch(
            mapAnnotationToMeasurement({
              annotationUID: uid,
              canonicalMeasurement: ai.canonicalMeasurement,
            })
          );

          count += 1;
        } catch {
          // Viewport not fully ready
        }
      });

    // ── AI annotations (FREEHAND — volumes traced as polylines) ──────
    // FREEHAND is overloaded: doppler VTI envelopes and LV Simpson-volume
    // contours both arrive as FREEHAND, distinguished only by the
    // canonicalMeasurementKey slug (`_vti` → doppler, else → volume). Without a
    // key there is no clear path, so we skip rather than guess and mis-draw.
    aiAnnotatedVariants
      .filter(
        (ai) =>
          ai.annotation &&
          ai.annotation.type === 'FREEHAND' &&
          !!ai.canonicalMeasurementKey &&
          ai.annotation.points.length > 0 &&
          ai.annotation.dicom === dicomUuid &&
          !humanAnnotatedKeys.has(ai.canonicalMeasurement) &&
          allowedCanonicals.has(ai.canonicalMeasurement)
      )
      .forEach((ai) => {
        const uid = aiHydratedAnnotationUid(studyUuid, ai.canonicalMeasurement);
        if (annotation.state.getAnnotation(uid)) {
          return;
        }
        // Same nav-back race guard as the AI LINE block above — skip
        // when the adopted human variant is already in state.
        if (
          annotation.state.getAnnotation(
            hydratedAnnotationUid(studyUuid, ai.canonicalMeasurement)
          )
        )
          return;

        const aiLabel =
          classToLabel.get(ai.canonicalMeasurement) ??
          ai.name ??
          ai.canonicalMeasurement;

        const ann = ai.annotation!;
        const refImageId =
          stackImageId ?? `video:${dicomUuid}/frames/${ann.frameNumber}`;

        // Seed the volume with the DISPLAY variant's value — what the
        // worksheet row shows. For volumes the display variant (e.g. computed
        // biplane LVEDV) can outrank the traced variant whose contour we draw;
        // seeding the trace's own value made the chip disagree with the
        // worksheet (77 vs 86 mL). A reshape recomputes Simpson and takes
        // over from there.
        const displayVariant = aiVariantRoles.get(
          ai.canonicalMeasurement
        )?.display;

        // Doppler VTI envelopes share the FREEHAND type with LV Simpson-volume
        // contours but need the doppler envelope renderer, not the Simpson path.
        const added = isDopplerVtiMeasurement(ai)
          ? hydrateAiDopplerVti({
              ai,
              uid,
              aiLabel,
              abbreviation: classToAbbreviation.get(ai.canonicalMeasurement),
              isSelected: selectedAnnotationUIDRef.current === uid,
              frameOfReferenceUID,
              viewport,
              camera,
              canEdit,
              dispatch,
            })
          : hydrateVolumeAnnotation({
              uid,
              label: `AI: ${aiLabel}`,
              isAI: true,
              canonicalMeasurement: ai.canonicalMeasurement,
              value: displayVariant?.value ?? ai.value ?? null,
              unit: displayVariant?.unit ?? ai.unit ?? 'mL',
              points: ann.points,
              dicomUuid,
              frameOfReferenceUID,
              refImageId,
              statsKey: stackImageId
                ? `imageId:${refImageId}`
                : `videoId:${refImageId}`,
              reorderOutline: true,
              viewport,
              camera,
              canEdit,
              pixelSpacing,
              dispatch,
              frameNumber: ann.frameNumber,
              abbreviation: classToAbbreviation.get(ai.canonicalMeasurement),
              isSelected: selectedAnnotationUIDRef.current === uid,
            });
        if (added) count += 1;
      });

    // ── AI annotations (AREA — em2 contour_* polygons) ───────────────
    // Ingestion owns this type, so no slug guessing: an ordered closed border,
    // drawn as-is with a cm² readout and no Simpson disks.
    // An area is one measurement on one plane: `annotated` is the best-ranked
    // variant that carries geometry, ranked across every plane, so the panel,
    // the row click and this all resolve to the same annotation.
    aiAnnotatedVariants.forEach((ai) => {
      const canonical = ai.canonicalMeasurement;
      if (
        !ai?.annotation ||
        ai.annotation.type !== 'AREA' ||
        ai.annotation.points.length === 0 ||
        humanAnnotatedKeys.has(canonical) ||
        !allowedCanonicals.has(canonical) ||
        ai.annotation.dicom !== dicomUuid
      )
        return;
      const uid = aiHydratedAnnotationUid(studyUuid, ai.canonicalMeasurement);
      if (annotation.state.getAnnotation(uid)) return;
      // Same nav-back race guard as the AI LINE/FREEHAND blocks above.
      if (
        annotation.state.getAnnotation(
          hydratedAnnotationUid(studyUuid, ai.canonicalMeasurement)
        )
      )
        return;

      const ann = ai.annotation;
      const refImageId =
        stackImageId ?? `video:${dicomUuid}/frames/${ann.frameNumber}`;

      // Same display-variant seeding as the volume block above: the chip must
      // show the worksheet row's value until the contour is reshaped.
      const displayVariant = aiVariantRoles.get(
        ai.canonicalMeasurement
      )?.display;

      const added = hydrateVolumeAnnotation({
        uid,
        label: `AI: ${
          classToLabel.get(ai.canonicalMeasurement) ??
          ai.name ??
          ai.canonicalMeasurement
        }`,
        isAI: true,
        canonicalMeasurement: ai.canonicalMeasurement,
        value: displayVariant?.value ?? ai.value ?? null,
        unit: displayVariant?.unit ?? ai.unit ?? 'cm2',
        points: ann.points,
        dicomUuid,
        frameOfReferenceUID,
        refImageId,
        statsKey: stackImageId
          ? `imageId:${refImageId}`
          : `videoId:${refImageId}`,
        reorderOutline: false,
        isArea: true,
        viewport,
        camera,
        canEdit,
        pixelSpacing,
        dispatch,
        frameNumber: ann.frameNumber,
        abbreviation: classToAbbreviation.get(ai.canonicalMeasurement),
        isSelected: selectedAnnotationUIDRef.current === uid,
      });
      if (added) count += 1;
    });

    // ── AI annotations (POINT — doppler peak-velocity markers) ───────
    aiAnnotatedVariants
      .filter(
        (ai) =>
          ai.annotation &&
          ai.annotation.type === 'POINT' &&
          ai.annotation.points.length > 0 &&
          ai.annotation.dicom === dicomUuid &&
          !humanAnnotatedKeys.has(ai.canonicalMeasurement) &&
          allowedCanonicals.has(ai.canonicalMeasurement)
      )
      .forEach((ai) => {
        const uid = aiHydratedAnnotationUid(studyUuid, ai.canonicalMeasurement);
        if (annotation.state.getAnnotation(uid)) return;
        // Same nav-back race guard as the AI LINE/FREEHAND blocks above.
        if (
          annotation.state.getAnnotation(
            hydratedAnnotationUid(studyUuid, ai.canonicalMeasurement)
          )
        )
          return;

        const added = hydrateAiDopplerPoint({
          ai,
          uid,
          aiLabel:
            classToLabel.get(ai.canonicalMeasurement) ??
            ai.name ??
            ai.canonicalMeasurement,
          abbreviation: classToAbbreviation.get(ai.canonicalMeasurement),
          isSelected: selectedAnnotationUIDRef.current === uid,
          frameOfReferenceUID,
          viewport,
          camera,
          canEdit,
          dispatch,
        });
        if (added) count += 1;
      });

    if (count > 0) {
      try {
        toolsUtilities.triggerAnnotationRenderForViewportIds([viewportId]);
      } catch {
        // Viewport may not be fully enabled
      }
    }

    hydratedKeyRef.current = key;
  }, [
    viewport,
    viewportId,
    isViewportReady,
    studyUuid,
    dicomUuid,
    videoUrl,
    isLoadingViewport,
    loadedDicomUuidRef,
    stackImageId,
    report,
    worksheet,
    canonicalMeasurements,
    dispatch,
    canEdit,
    pixelSpacing,
    view,
    userId,
  ]);
}
